/* Companion outbound-diagnostics amber banner — Sep-2026.
 *
 * Polls /api/admin/companion-integration/companion-app-last-diagnostic
 * once at mount and every 5 min. Surfaces an amber banner when the
 * cron probe last reported the peer rejecting our key (auth stage) or
 * anything else non-ok. Silent when the last probe was healthy.
 */
import React, { useEffect, useState } from 'react';
import { AlertTriangle, Activity, CircleOff, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/api';

const STAGE_COPY = {
  config:  { label: 'not configured',      hint: 'The Indie Reader URL or API key is empty. Fill both in Admin → Operations → API Keys → Indie Reader Location.' },
  auth:    { label: 'rejecting our key',    hint: 'The Indie Reader app returned 401/403. Rotate INDIE_READER_SERVICE_KEY on their side and paste the new value below.' },
  network: { label: 'unreachable',          hint: 'DNS / TLS / timeout. Confirm the Indie Reader URL is correct and the Reader backend is live.' },
  books:   { label: 'returning an error',   hint: 'The peer answered but returned a non-2xx status. Inspect the Indie Reader logs for the corresponding request.' },
};

function ageMs(iso) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Date.now() - t;
}
function ageLabel(iso) {
  const ms = ageMs(iso);
  if (ms == null) return 'never';
  const m = Math.round(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}

export default function CompanionDiagBanner() {
  const [state, setState] = useState({ loading: true });
  const check = async (refresh = false) => {
    try {
      const r = refresh
        ? await adminApi.companionAppDiagnostics()
        : await adminApi.companionAppLastDiag();
      setState({ loading: false, ...r });
    } catch (e) {
      setState({ loading: false, ok: false, stage: 'network',
                 message: e?.response?.data?.detail || 'Could not read diagnostic state.',
                 at: null });
    }
  };
  useEffect(() => {
    check();
    const t = window.setInterval(check, 5 * 60_000);
    return () => window.clearInterval(t);
  }, []);

  if (state.loading) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-[11.5px] text-muted-foreground"
           data-testid="companion-diag-loading">
        <Loader2 className="h-3 w-3 animate-spin" />
        Checking Indie Reader connection…
      </div>
    );
  }
  // Never probed yet — stay quiet; the hourly cron will populate soon.
  if (!state.at) return null;

  if (state.configured === false || state.stage === 'config') {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-[11.5px] text-muted-foreground"
           data-testid="companion-diag-not-configured">
        <CircleOff className="h-3.5 w-3.5" />
        <span className="font-semibold text-foreground">Indie Reader · Not configured</span>
        <span>No outage alarm is active.</span>
        <Link to="/admin/operations"
              data-testid="companion-diag-configure-link"
              className="ml-auto font-semibold underline decoration-dotted underline-offset-2">
          Configure →
        </Link>
      </div>
    );
  }

  // Healthy: tiny green chip, no chrome.
  if (state.ok) {
    return (
      <div className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10.5px] text-emerald-700 dark:text-emerald-400"
           data-testid="companion-diag-ok"
           title={`Last check: ${ageLabel(state.at)} — ${state.message || 'OK'}`}>
        <Activity className="h-3 w-3" />
        Indie Reader · reachable
      </div>
    );
  }

  const copy = STAGE_COPY[state.stage] || { label: state.stage || 'error', hint: state.message };
  return (
    <div className="rounded-md border border-amber-500/60 bg-amber-500/10 px-3 py-2 text-[12px] text-amber-900 dark:text-amber-200"
         data-testid="companion-diag-warn">
      <div className="flex items-center gap-2 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5" />
        Indie Reader — {copy.label}
        <span className="ml-1 rounded-full bg-amber-500/20 px-1.5 py-0 text-[10px] font-normal uppercase tracking-wider">
          {state.trigger || 'cron'} · {ageLabel(state.at)}
        </span>
      </div>
      <div className="mt-0.5 pl-5 text-[11px] opacity-90">
        {copy.hint}
        {state.message ? <> — <em>{state.message.slice(0, 200)}</em></> : null}
        {'  '}
        <Link to="/admin/operations"
              data-testid="companion-diag-fix-link"
              className="font-semibold underline decoration-amber-400 hover:decoration-amber-600">
          Open API Keys →
        </Link>
        {'  '}
        <button type="button" onClick={() => check(true)}
                data-testid="companion-diag-recheck"
                className="underline decoration-dotted underline-offset-2 hover:decoration-solid">
          re-check now
        </button>
      </div>
    </div>
  );
}
